import React, { createContext, useContext, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useCatalogBrandId } from './catalogBrandContext';

/**
 * Every catalog `<img>` of a brand's own product photography.
 *
 * Brand images are full-resolution — the Ivonne catalogue's are 2640x4048 and
 * ~2 MB each — and these surfaces draw them into 36px-200px boxes. The Parent
 * Bulk grid renders 48 cards at once, so one screen used to pull ~97 MB of
 * pixels. The API generates a 400px webp at ingest (R8); this component
 * resolves the full-resolution URL to it through one cached map.
 *
 * It exists as a component rather than a `thumbnailUrl` field on each payload
 * because the render sites had already drifted apart — three carried
 * `loading="lazy"` and `decoding="async"` and `FamilyCardMedia` carried
 * neither. One component is one place for those to be right.
 *
 * Fallbacks, in order: thumbnail -> full-resolution URL -> `onBroken`. A URL
 * with no thumbnail (pass not run, or no object store configured) renders the
 * full-resolution image exactly as before, so nothing here is load-bearing.
 */

export interface CatalogThumbnailsValue {
  /** Source URL -> thumbnail URL. Absent keys have no thumbnail. */
  map: Record<string, string>;
  /** False only while the map is still loading. */
  settled: boolean;
}

/**
 * Default: no thumbnails, already settled. So a `CatalogImage` rendered
 * OUTSIDE the provider — in a unit test, or on a surface that never mounts it
 * — renders the full-resolution URL exactly as the raw `<img>` used to, rather
 * than requiring a tRPC context to draw a picture.
 */
const CatalogThumbnailContext = createContext<CatalogThumbnailsValue>({ map: {}, settled: true });

export const useCatalogThumbnails = (): CatalogThumbnailsValue =>
  useContext(CatalogThumbnailContext);

/**
 * Fetches the map once for the subtree. Mounted at the catalog route, so every
 * catalog surface shares one cached request.
 */
export const CatalogThumbnailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brandId = useCatalogBrandId();
  const q = trpc.catalog.getThumbnailMap.useQuery({ brandId });
  const value = useMemo(
    // An errored map counts as settled: a failed request must not hide every
    // image on the page forever — it falls back to full resolution.
    () => ({ map: (q.data ?? {}) as Record<string, string>, settled: !q.isLoading }),
    [q.data, q.isLoading],
  );
  return (
    <CatalogThumbnailContext.Provider value={value}>{children}</CatalogThumbnailContext.Provider>
  );
};

interface CatalogImageProps {
  /** The catalog's full-resolution image URL. */
  src: string;
  alt?: string;
  className?: string;
  /** Both the thumbnail and the full-resolution URL failed to load. */
  onBroken?: () => void;
  /**
   * Render the full-resolution bytes, never the thumbnail. Only the image
   * readiness panel passes this: that panel exists to judge background and
   * whiteness, so a 400px re-encode would be the wrong evidence for the
   * verdict it renders beside the picture. Do not "optimise" it later.
   */
  full?: boolean;
  'data-testid'?: string;
}

const CatalogImage: React.FC<CatalogImageProps> = ({
  src,
  alt = '',
  className,
  onBroken,
  full = false,
  'data-testid': testId,
}) => {
  const { map, settled } = useCatalogThumbnails();
  const [thumbFailed, setThumbFailed] = useState(false);

  const thumb = full || thumbFailed ? null : (map[src] ?? null);

  // Until the map settles we do not know whether a thumbnail exists. Rendering
  // the full-resolution URL in the meantime would download the 2 MB file we are
  // here to avoid, so hold the box instead — the caller's className sizes it,
  // so nothing shifts when the image arrives.
  if (!full && !settled) {
    return <div data-testid={testId} className={`${className ?? ''} bg-gray-50`} aria-hidden="true" />;
  }

  return (
    <img
      data-testid={testId}
      src={thumb ?? src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        // A stored thumbnail that 404s falls back to the original before we
        // call the image broken.
        if (thumb) setThumbFailed(true);
        else onBroken?.();
      }}
      className={className}
    />
  );
};

export default CatalogImage;
